// Part Master — Feb 2026 restructure (v2, polished layout).
//
// Identification view over the shared `parts` collection. Shows the
// fields the sales/customer team cares about; RM & routing configuration
// live on BOM Routing (Item Master).

import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Layout from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import MasterExcelToolbar from "../components/MasterExcelToolbar";
import { exportWithHeader } from "../utils/excelExport";
import { PencilSimple, MagnifyingGlass, Plus, Package, FunnelSimple, DownloadSimple } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// A part is considered "in BOM Routing" if its routing has been configured
// (operations_flow non-empty) OR it has a raw-material code assigned. Parts
// registered from imports but never opened in BOM Routing surface as
// "orphan" identifiers here, useful for gap-audits before a costing run.
function hasBomRouting(p) {
  const flow = Array.isArray(p?.operations_flow) ? p.operations_flow.filter(Boolean) : [];
  if (flow.length > 0) return true;
  if ((p?.rm_code || "").trim()) return true;
  return false;
}

export default function PartMaster() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // "notInBom" filter — when true, hide parts whose BOM Routing is set up.
  // Toggle sits in the toolbar so operators can audit un-configured parts
  // before running a cost sheet / RM plan.
  const [notInBom, setNotInBom] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/parts`);
      setParts(r.data);
    } catch (e) { toast.error("Failed to load parts: " + (e.message || "")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = parts.filter(p => {
    if (notInBom && hasBomRouting(p)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (p.part_no || "").toLowerCase().includes(q) ||
           (p.description || "").toLowerCase().includes(q) ||
           (p.company_part_no || "").toLowerCase().includes(q) ||
           (p.company_part_no_description || "").toLowerCase().includes(q) ||
           (p.drawing_no || "").toLowerCase().includes(q) ||
           (p.revision_no || "").toLowerCase().includes(q) ||
           (p.customer || "").toLowerCase().includes(q);
  });

  // Export whichever set is currently visible. When "Not in BOM Routing" is
  // active the export therefore contains ONLY the orphan parts — matches
  // user's Feb 2026 request.
  const exportFiltered = () => {
    if (filtered.length === 0) { toast.error("Nothing to export"); return; }
    const rows = filtered.map(p => ({
      "Part No":                    p.part_no || "",
      "Customer":                   p.customer || "",
      "Customer Part Description":  p.description || "",
      "Company Part No":            p.company_part_no || "",
      "Company Part Description":   p.company_part_no_description || "",
      "Drawing No":                 p.drawing_no || "",
      "Rev":                        p.revision_no || "",
      "Finished Wt (Kg)":           p.finished_weight ?? p.gross_weight ?? "",
      "Part Rate (₹)":              p.part_rate ?? "",
      "BOM Routing Configured":     hasBomRouting(p) ? "Yes" : "No",
    }));
    const suffix = notInBom ? "Not-in-BOM-Routing" : "All";
    exportWithHeader(
      [{ name: "Parts", rows }],
      `Part-Master_${suffix}_${new Date().toISOString().slice(0,10)}.xlsx`,
      notInBom
        ? "Parts registered in Part Master but NOT configured in BOM Routing"
        : "Every part in Part Master"
    );
    toast.success(`Exported ${rows.length} part${rows.length === 1 ? "" : "s"}`);
  };

  const openNew = () => {
    setEditing({
      part_no: "", customer: "", description: "",
      company_part_no: "", company_part_no_description: "",
      drawing_no: "", revision_no: "",
      finished_weight: null, part_rate: null,
    });
    setDialogOpen(true);
  };
  const openEdit = (p) => { setEditing({ ...p }); setDialogOpen(true); };

  const save = async () => {
    if (!editing) return;
    if (!(editing.part_no || "").trim()) return toast.error("Part No is required");
    if (!(editing.customer || "").trim()) return toast.error("Customer is required");
    try {
      const isNew = !editing.id;
      const payload = { ...editing };
      if (isNew) {
        if (!(payload.description || "").trim()) payload.description = payload.part_no.trim();
        delete payload.id;
        await axios.post(`${API}/parts`, payload);
        toast.success("Part created");
      } else {
        await axios.put(`${API}/parts/${editing.id}`, payload);
        toast.success("Part updated");
      }
      setDialogOpen(false); setEditing(null); load();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Layout title="Part Master">
      <div className="p-6 space-y-5 max-w-[1400px] mx-auto">
        <PageHeader
          icon={Package}
          title="Part Master"
          subtitle="Identification fields for every part. Raw-material, operations and BOM live in BOM Routing."
          rightSlot={
            <div className="flex items-center gap-2 flex-wrap">
              <MasterExcelToolbar basePath="/part-master" entityLabel="Parts" onImportDone={load} size="sm" />
              <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white gap-1 h-9" onClick={openNew} data-testid="add-part-btn">
                <Plus size={14} /> New Part
              </Button>
            </div>
          }
        />

        <Card className="p-2 border border-[#E2E8F0] flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={14} className="text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by part no, description, company part no, customer…"
              className="min-h-[38px] pl-9 border-transparent focus-visible:border-[#CBD5E1]"
              data-testid="part-master-search" />
          </div>
          <button
            type="button"
            onClick={() => setNotInBom(v => !v)}
            className={`px-3 h-9 rounded-md text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 border transition
              ${notInBom
                ? "bg-[#B45309] border-[#B45309] text-white"
                : "bg-white border-[#E2E8F0] text-[#334155] hover:border-[#1D2951]"}`}
            data-testid="part-master-filter-not-in-bom"
            title="Show only parts whose BOM Routing is not yet configured"
          >
            <FunnelSimple size={14} weight={notInBom ? "fill" : "regular"} />
            Not in BOM Routing
          </button>
          <button
            type="button"
            onClick={exportFiltered}
            className="px-3 h-9 rounded-md text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 border bg-[#1D2951] border-[#1D2951] text-white hover:bg-[#2A3B73]"
            data-testid="part-master-export-filtered"
            title={notInBom ? "Export the not-in-BOM list" : "Export the visible list"}
          >
            <DownloadSimple size={14} weight="bold" />
            Export {notInBom ? "Filtered" : "Visible"}
          </button>
          <span className="text-[11px] text-[#64748B] font-semibold pr-2">
            {filtered.length} / {parts.length} parts
          </span>
        </Card>

        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="part-master-table">
              <thead className="bg-[#F8FAFC] text-[#475569] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Part No</th>
                  <th className="px-3 py-3 text-left font-semibold">Customer</th>
                  <th className="px-3 py-3 text-left font-semibold">Customer Part Description</th>
                  <th className="px-3 py-3 text-left font-semibold">Company Part No</th>
                  <th className="px-3 py-3 text-left font-semibold">Company Part Description</th>
                  <th className="px-3 py-3 text-left font-semibold">Drawing No</th>
                  <th className="px-3 py-3 text-left font-semibold">Rev</th>
                  <th className="px-3 py-3 text-right font-semibold">Finished Wt (Kg)</th>
                  <th className="px-3 py-3 text-right font-semibold">Part Rate (₹)</th>
                  <th className="px-3 py-3 text-center font-semibold">BOM</th>
                  <th className="px-3 py-3 text-center font-semibold w-16">Edit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && (
                  <tr><td colSpan={11} className="py-10 text-center text-[#64748B]">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={11} className="py-12 text-center text-[#94A3B8]">{parts.length === 0 ? <>No parts yet — click <b>New Part</b> to add one.</> : (notInBom ? "Every part has BOM Routing configured — nothing to show." : "No parts match your search.")}</td></tr>
                )}
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-[#F8FAFC]" data-testid={`part-master-row-${p.part_no}`}>
                    <td className="px-4 py-3 font-mono font-bold text-[#0F172A]">{p.part_no}</td>
                    <td className="px-3 py-3 text-[#334155]">{p.customer || "—"}</td>
                    <td className="px-3 py-3 text-[#475569] truncate max-w-[260px]" title={p.description}>{p.description || "—"}</td>
                    <td className="px-3 py-3 font-mono text-[#1D4ED8]">{p.company_part_no || "—"}</td>
                    <td className="px-3 py-3 text-[#475569] truncate max-w-[260px]" title={p.company_part_no_description}>{p.company_part_no_description || "—"}</td>
                    <td className="px-3 py-3 font-mono text-[#334155]">{p.drawing_no || "—"}</td>
                    <td className="px-3 py-3 font-mono text-[#334155]">{p.revision_no || "—"}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#334155]">{p.finished_weight != null ? p.finished_weight : (p.gross_weight ?? "—")}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#334155]">₹{(p.part_rate ?? 0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-center">
                      {hasBomRouting(p) ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#DCFCE7] text-[#166534] font-black text-[11px]">✓</span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FEE2E2] text-[#B91C1C] font-black text-[11px]">✕</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-part-${p.part_no}`}>
                        <PencilSimple size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <p className="text-[11px] text-[#94A3B8]">
          Raw-material specs, operations and BOM routing for each part live in <b className="text-[#1D2951]">BOM Routing</b>.
        </p>
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? `Edit Part — ${editing.part_no}` : "New Part"}</DialogTitle></DialogHeader>
          {editing && (
            <>
              <div className="space-y-4 pt-2">
                <FormSection title="Identity">
                  <FieldGrid cols={2}>
                    <Field label="Part No *">
                      <Input value={editing.part_no || ""} onChange={e => setEditing({ ...editing, part_no: e.target.value })} data-testid="edit-part-no" />
                    </Field>
                    <Field label="Customer *">
                      <CustomerSelect value={editing.customer || ""}
                        onChange={(v) => setEditing({ ...editing, customer: v })}
                        testId="edit-customer" />
                    </Field>
                    <Field label="Customer Part Description *" span={2}>
                      <Input value={editing.description || ""} onChange={e => setEditing({ ...editing, description: e.target.value })} data-testid="edit-description" />
                    </Field>
                  </FieldGrid>
                </FormSection>

                <FormSection title="Company Reference" hint="optional — for shared internal part numbers">
                  <FieldGrid cols={2}>
                    <Field label="Company Part No">
                      <Input value={editing.company_part_no || ""} onChange={e => setEditing({ ...editing, company_part_no: e.target.value })} data-testid="edit-company-part-no" />
                    </Field>
                    {(editing.company_part_no || "").trim() && (
                      <Field label="Company Part Description">
                        <Input value={editing.company_part_no_description || ""} onChange={e => setEditing({ ...editing, company_part_no_description: e.target.value })} data-testid="edit-company-part-no-description" />
                      </Field>
                    )}
                  </FieldGrid>
                </FormSection>

                <FormSection title="Engineering Drawing" hint="manually entered from the drawing PDF">
                  <FieldGrid cols={2}>
                    <Field label="Drawing Number">
                      <Input value={editing.drawing_no || ""}
                        onChange={e => setEditing({ ...editing, drawing_no: e.target.value })}
                        placeholder="e.g. DRW-1024"
                        className="font-mono"
                        data-testid="edit-drawing-no" />
                    </Field>
                    <Field label="Revision Number">
                      <Input value={editing.revision_no || ""}
                        onChange={e => setEditing({ ...editing, revision_no: e.target.value })}
                        placeholder="e.g. R02"
                        className="font-mono"
                        data-testid="edit-revision-no" />
                    </Field>
                  </FieldGrid>
                </FormSection>

                <FormSection title="Commercial">
                  <FieldGrid cols={2}>
                    <Field label="Finished Weight (Kg)">
                      <Input type="number" step="0.001" value={editing.finished_weight ?? editing.gross_weight ?? ""}
                        onChange={e => setEditing({ ...editing, finished_weight: e.target.value === "" ? null : parseFloat(e.target.value) })}
                        data-testid="edit-finished-weight" />
                    </Field>
                    <Field label="Part Rate (₹)">
                      <Input type="number" step="0.01" value={editing.part_rate ?? ""}
                        onChange={e => setEditing({ ...editing, part_rate: e.target.value === "" ? null : parseFloat(e.target.value) })}
                        data-testid="edit-part-rate" />
                    </Field>
                  </FieldGrid>
                </FormSection>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E8F0] mt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button className="bg-[#1D2951] text-white" onClick={save} data-testid="save-part">
                  <Plus size={14} /> {editing.id ? "Save Changes" : "Create Part"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function PageHeader({ icon: Icon, title, subtitle, rightSlot }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3">
        {Icon && <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951]"><Icon size={20} weight="fill" /></div>}
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>{title}</h1>
          {subtitle && <p className="text-xs text-[#64748B] mt-0.5 max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      {rightSlot}
    </div>
  );
}
function FormSection({ title, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">{title}</h3>
        {hint && <span className="text-[10px] text-[#94A3B8] italic">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
function FieldGrid({ cols = 2, children }) {
  const grid = { 2: "grid-cols-2", 3: "grid-cols-3" }[cols] || "grid-cols-2";
  return <div className={`grid ${grid} gap-3`}>{children}</div>;
}
function Field({ label, hint, span = 1, children }) {
  const spanCls = span === 2 ? "col-span-2" : "";
  return (
    <div className={spanCls}>
      <Label className="text-[11px] font-semibold text-[#334155] flex items-center gap-1">
        {label}{hint && <span className="text-[10px] font-normal text-[#94A3B8]">({hint})</span>}
      </Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
